import * as React from 'react';
import type { Edge, StepNode } from '../../types';

export function useWiringPaths({
  orientation,
  nodes,
  edges,
  usedInputsByStepId,
  wiredOutputs,
  containerRef,
  inputElByKeyRef,
  outputElByKeyRef,
  stepElByIdRef,
}: {
  orientation: 'horizontal' | 'vertical';
  nodes: StepNode[];
  edges: Edge[];
  usedInputsByStepId: Map<string, Set<string>>;
  wiredOutputs: Array<{ outKey: string; stepId: string; outputKey: string }>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  inputElByKeyRef: React.RefObject<Map<string, HTMLDivElement | null>>;
  outputElByKeyRef: React.RefObject<Map<string, HTMLDivElement | null>>;
  stepElByIdRef: React.RefObject<Map<string, HTMLButtonElement | null>>;
}) {
  const rafRef = React.useRef<number | null>(null);
  const [inputWiringPaths, setInputWiringPaths] = React.useState<Array<{ id: string; d: string }>>([]);
  const [outputWiringPaths, setOutputWiringPaths] = React.useState<Array<{ id: string; d: string }>>([]);
  const [edgePaths, setEdgePaths] = React.useState<Array<{ id: string; d: string }>>([]);
  const [edgeMidpoints, setEdgeMidpoints] = React.useState<Array<{ id: string; x: number; y: number }>>([]);
  const [inputWiringMidpoints, setInputWiringMidpoints] = React.useState<Array<{ id: string; x: number; y: number }>>([]);

  const bezierPoint = React.useCallback((t: number, p0: number, p1: number, p2: number, p3: number) => {
    const u = 1 - t;
    return (u * u * u) * p0
      + (3 * u * u * t) * p1
      + (3 * u * t * t) * p2
      + (t * t * t) * p3;
  }, []);

  const scheduleWiringUpdate = React.useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null;

      const container = containerRef.current;
      if (!container) {
        setInputWiringPaths([]);
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const paths: Array<{ id: string; d: string }> = [];
      const outPaths: Array<{ id: string; d: string }> = [];
      const edgeDs: Array<{ id: string; d: string }> = [];
      const edgeMids: Array<{ id: string; x: number; y: number }> = [];
      const inputMids: Array<{ id: string; x: number; y: number }> = [];

      edges.forEach((e) => {
        const fromEl = stepElByIdRef.current.get(e.from);
        const toEl = stepElByIdRef.current.get(e.to);
        if (!fromEl || !toEl) return;

        const a = fromEl.getBoundingClientRect();
        const b = toEl.getBoundingClientRect();

        if (orientation === 'vertical') {
          const x1 = a.left + a.width / 2 - containerRect.left;
          const y1 = a.bottom - containerRect.top + 2;
          const x2 = b.left + b.width / 2 - containerRect.left;
          const y2 = b.top - containerRect.top - 2;

          if (y2 <= y1 + 4) return;
          const dy = Math.max(24, (y2 - y1) * 0.5);
          const c1y = y1 + dy;
          const c2y = y2 - dy;
          const d = `M ${x1} ${y1} C ${x1} ${c1y}, ${x2} ${c2y}, ${x2} ${y2}`;
          edgeDs.push({ id: e.id, d });

          // Midpoint on cubic bezier at t=0.5
          const t = 0.5;
          const mx = bezierPoint(t, x1, x1, x2, x2);
          const my = bezierPoint(t, y1, c1y, c2y, y2);
          edgeMids.push({ id: e.id, x: mx, y: my });
          return;
        }

        const x1 = a.right - containerRect.left + 2;
        const y1 = a.top + a.height / 2 - containerRect.top;
        const x2 = b.left - containerRect.left - 2;
        const y2 = b.top + b.height / 2 - containerRect.top;

        if (x2 <= x1 + 4) return;
        const dx = Math.max(24, (x2 - x1) * 0.5);
        const c1x = x1 + dx;
        const c2x = x2 - dx;
        const d = `M ${x1} ${y1} C ${c1x} ${y1}, ${c2x} ${y2}, ${x2} ${y2}`;
        edgeDs.push({ id: e.id, d });

        // Midpoint on cubic bezier at t=0.5
        const t = 0.5;
        const mx = bezierPoint(t, x1, c1x, c2x, x2);
        const my = bezierPoint(t, y1, y1, y2, y2);
        edgeMids.push({ id: e.id, x: mx, y: my });
      });

      const pairs: Array<{ inputKey: string; stepId: string }> = [];
      Array.from(usedInputsByStepId.entries()).forEach(([stepId, keys]) => {
        Array.from(keys).forEach((inputKey) => pairs.push({ inputKey, stepId }));
      });
      pairs.sort((a, b) => (a.inputKey === b.inputKey ? a.stepId.localeCompare(b.stepId) : a.inputKey.localeCompare(b.inputKey)));

      pairs.forEach(({ inputKey, stepId }) => {
        const inputEl = inputElByKeyRef.current.get(inputKey);
        const stepEl = stepElByIdRef.current.get(stepId);
        if (!inputEl || !stepEl) return;

        const a = inputEl.getBoundingClientRect();
        const b = stepEl.getBoundingClientRect();

        if (orientation === 'vertical') {
          const x1 = a.left + a.width / 2 - containerRect.left;
          const y1 = a.bottom - containerRect.top + 2;
          const x2 = b.left + b.width / 2 - containerRect.left;
          const y2 = b.top - containerRect.top - 2;

          if (y2 <= y1 + 4) return;
          const dy = Math.max(24, (y2 - y1) * 0.5);
          const c1y = y1 + dy;
          const c2y = y2 - dy;
          const d = `M ${x1} ${y1} C ${x1} ${c1y}, ${x2} ${c2y}, ${x2} ${y2}`;
          const id = `${inputKey}->${stepId}`;
          paths.push({ id, d });

          const t = 0.5;
          const mx = bezierPoint(t, x1, x1, x2, x2);
          const my = bezierPoint(t, y1, c1y, c2y, y2);
          inputMids.push({ id, x: mx, y: my });
          return;
        }

        const x1 = a.right - containerRect.left + 2;
        const y1 = a.top + a.height / 2 - containerRect.top;
        const x2 = b.left - containerRect.left - 2;
        const y2 = b.top + b.height / 2 - containerRect.top;

        if (x2 <= x1 + 4) return;
        const dx = Math.max(24, (x2 - x1) * 0.5);
        const c1x = x1 + dx;
        const c2x = x2 - dx;
        const d = `M ${x1} ${y1} C ${c1x} ${y1}, ${c2x} ${y2}, ${x2} ${y2}`;
        const id = `${inputKey}->${stepId}`;
        paths.push({ id, d });

        const t = 0.5;
        const mx = bezierPoint(t, x1, c1x, c2x, x2);
        const my = bezierPoint(t, y1, y1, y2, y2);
        inputMids.push({ id, x: mx, y: my });
      });

      wiredOutputs.forEach(({ outKey, stepId, outputKey }) => {
        const stepEl = stepElByIdRef.current.get(stepId);
        const outputEl = outputElByKeyRef.current.get(outKey);
        if (!stepEl || !outputEl) return;

        const a = stepEl.getBoundingClientRect();
        const b = outputEl.getBoundingClientRect();

        if (orientation === 'vertical') {
          const x1 = a.left + a.width / 2 - containerRect.left;
          const y1 = a.bottom - containerRect.top + 2;
          const x2 = b.left + b.width / 2 - containerRect.left;
          const y2 = b.top - containerRect.top - 2;

          if (y2 <= y1 + 4) return;
          const dy = Math.max(24, (y2 - y1) * 0.5);
          const c1y = y1 + dy;
          const c2y = y2 - dy;
          const d = `M ${x1} ${y1} C ${x1} ${c1y}, ${x2} ${c2y}, ${x2} ${y2}`;
          outPaths.push({ id: `${stepId}->${outKey}:${outputKey}`, d });
          return;
        }

        const x1 = a.right - containerRect.left + 2;
        const y1 = a.top + a.height / 2 - containerRect.top;
        const x2 = b.left - containerRect.left - 2;
        const y2 = b.top + b.height / 2 - containerRect.top;

        if (x2 <= x1 + 4) return;
        const dx = Math.max(24, (x2 - x1) * 0.5);
        const c1x = x1 + dx;
        const c2x = x2 - dx;
        const d = `M ${x1} ${y1} C ${c1x} ${y1}, ${c2x} ${y2}, ${x2} ${y2}`;
        outPaths.push({ id: `${stepId}->${outKey}:${outputKey}`, d });
      });

      setInputWiringPaths(paths);
      setOutputWiringPaths(outPaths);
      setEdgePaths(edgeDs);
      setEdgeMidpoints(edgeMids);
      setInputWiringMidpoints(inputMids);
    });
  }, [bezierPoint, containerRef, edges, inputElByKeyRef, orientation, outputElByKeyRef, stepElByIdRef, usedInputsByStepId, wiredOutputs]);

  React.useEffect(() => {
    scheduleWiringUpdate();
  }, [scheduleWiringUpdate, nodes.length, edges.length]);

  React.useEffect(() => {
    const onResize = () => scheduleWiringUpdate();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [scheduleWiringUpdate]);

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      scheduleWiringUpdate();
    });

    // Observe the container plus any currently registered endpoints.
    // This ensures that async content loading (which changes card sizes)
    // automatically triggers a wiring recompute without requiring a click.
    ro.observe(container);

    stepElByIdRef.current.forEach((el) => {
      if (el) ro.observe(el);
    });
    inputElByKeyRef.current.forEach((el) => {
      if (el) ro.observe(el);
    });
    outputElByKeyRef.current.forEach((el) => {
      if (el) ro.observe(el);
    });

    // Kick once in case the DOM is already laid out.
    scheduleWiringUpdate();

    return () => {
      ro.disconnect();
    };
    // Intentionally depend on high-level shape changes; ResizeObserver covers dynamic resizes.
  }, [scheduleWiringUpdate, containerRef, stepElByIdRef, inputElByKeyRef, outputElByKeyRef, nodes.length, edges.length, wiredOutputs.length]);

  return { edgePaths, edgeMidpoints, inputWiringPaths, inputWiringMidpoints, outputWiringPaths, scheduleWiringUpdate };
}
